/* ──────────────────────────────────────────────────────────────────────────
 * IPO Workspace Onboarding — standalone module.
 *
 * ISOLATED, SEPARATE BUILD. Faithful 1:1 React port of
 * `claude - ipo_workspace_onboarding_form.html`. Opened from the "Enter my IPO
 * workspace →" card on the Dashboard - New landing screen. Styles are fully
 * scoped under `.ipo-ob-root` in `IPOWorkspaceOnboarding.css` so they never
 * collide with the rest of the IPOLabs application. Tabler icons + Playfair/DM Sans
 * loaded in public/index.html.
 * ────────────────────────────────────────────────────────────────────────── */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import "./IPOWorkspaceOnboarding.css";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CRITERIA = {
  mb: [
    { n: "Post-issue paid-up equity capital ≥ ₹10 Cr", r: "with min post-issue market cap ≥ ₹25 Cr" },
    { n: "Avg pre-tax operating profit ≥ ₹15 Cr", r: "in 3 of last 5 years, profit in each of those 3" },
    { n: "Net tangible assets ≥ ₹3 Cr", r: "each of preceding 3 FYs; ≤50% in cash unless pure OFS" },
    { n: "Net worth ≥ ₹1 Cr", r: "in each of the preceding 3 full financial years" },
    { n: "Operational track record ≥ 3 years", r: "continuous operating history" },
  ],
  sme: [
    { n: "Post-issue paid-up capital ≤ ₹25 Cr", r: "must not exceed the SME cap" },
    { n: "Operational track record ≥ 3 years", r: "continuous operating history" },
    { n: "Net tangible assets ₹1.5–3 Cr", r: "depending on the exchange" },
    { n: "Positive EBITDA in 2 of last 3 years", r: "operating profitability" },
    { n: "Net worth ₹1–1.5 Cr", r: "in the preceding financial years" },
    { n: "Debt-to-equity ratio ≤ 3:1", r: "leverage within limit" },
    { n: "100% underwritten + market maker", r: "two-way quotes post-listing for stipulated period" },
  ],
};

const IPOWorkspaceOnboarding = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState("mb"); // "mb" | "sme"
  const [answers, setAnswers] = useState({}); // { [index]: "yes"|"no"|"unsure" }
  const [company, setCompany] = useState({ legal_name: "", website: "", email: "", mobile: "", gst: "", cin: "" });
  const [saving, setSaving] = useState(false);

  const setField = (k, v) => setCompany((p) => ({ ...p, [k]: v }));

  const pick = (m) => {
    if (m === mode) return;
    setMode(m);
    setAnswers({}); // reset eligibility answers when listing type changes
  };

  const setAns = (i, val) => setAnswers((prev) => ({ ...prev, [i]: val }));

  const items = CRITERIA[mode];
  const met = Object.values(answers).filter((v) => v === "yes").length;

  const submitForm = async () => {
    if (!company.legal_name.trim() || !company.mobile.trim() || !company.email.trim() || !company.gst.trim() || !company.cin.trim()) {
      toast.error("Please fill company name, email, mobile, GST and CIN.");
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(company.email.trim())) {
      toast.error("Please enter a valid company contact email.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/workspace`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          company,
          listing_type: mode,
          eligibility: { mode, answers, met, total: items.length },
        }),
      });
      if (!res.ok) {
        let msg = "Could not create your workspace. Please try again.";
        try { const d = await res.json(); if (typeof d.detail === "string") msg = d.detail; } catch { /* ignore */ }
        toast.error(msg);
        setSaving(false);
        return;
      }
      toast.success("Your IPO workspace is ready.");
      navigate("/ipo-navigator");
    } catch {
      toast.error("Could not create your workspace. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className="ipo-ob-root" data-testid="ipo-onboarding-page">
      {/* Minimal module chrome for back navigation */}
      <div className="iob-topbar">
        <div className="iob-brand" onClick={() => navigate("/dashboard-new")} data-testid="iob-brand">
          IPO<span>Navigator</span>
        </div>
        <div className="iob-eyebrow">IPO Workspace Setup</div>
        <div className="iob-back" onClick={() => navigate("/dashboard-new")} data-testid="iob-back">
          <i className="ti ti-arrow-left" aria-hidden="true" /> Back to dashboard
        </div>
      </div>

      <div className="iob-shell">
        <div className="ob">
          <h2 className="sr-only">
            IPO workspace setup form — collects company details (legal name, website, mobile, GST,
            CIN) and IPO type, then shows an adaptive SEBI eligibility self-assessment for Main Board
            or SME, ending in a submit button.
          </h2>

          <div className="ob-head">
            <div className="ob-kick">Step 1 of 6 · Workspace setup</div>
            <div className="ob-h">Let&apos;s set up your IPO workspace</div>
            <div className="ob-sub">
              A few details about your company, then a quick eligibility check against SEBI&apos;s
              criteria. This shapes your entire roadmap — you can refine everything later.
            </div>
          </div>

          <div className="ob-card">
            <div className="sec-t">
              <i className="ti ti-building" aria-hidden="true" /> Company details
            </div>

            <div className="fg">
              <label className="fl">
                Registered legal name <span className="req">*</span>
              </label>
              <input className="fi" placeholder="e.g. Acme Seeds Private Limited" value={company.legal_name} onChange={(e) => setField("legal_name", e.target.value)} data-testid="ob-legal-name" />
            </div>

            <div className="row2">
              <div className="fg">
                <label className="fl">Company website</label>
                <input className="fi" placeholder="https://" value={company.website} onChange={(e) => setField("website", e.target.value)} data-testid="ob-website" />
              </div>
              <div className="fg">
                <label className="fl">
                  Mobile number <span className="req">*</span>
                </label>
                <input className="fi" placeholder="+91" value={company.mobile} onChange={(e) => setField("mobile", e.target.value)} data-testid="ob-mobile" />
              </div>
            </div>

            <div className="fg" style={{ marginTop: 4 }}>
              <label className="fl">
                Company contact email <span className="req">*</span>
              </label>
              <input className="fi" type="email" placeholder="founder@company.com" value={company.email} onChange={(e) => setField("email", e.target.value)} data-testid="ob-email" />
            </div>

            <div className="row2">
              <div className="fg">
                <label className="fl">
                  GST number <span className="req">*</span>
                </label>
                <input className="fi" placeholder="22AAAAA0000A1Z5" value={company.gst} onChange={(e) => setField("gst", e.target.value)} data-testid="ob-gst" />
              </div>
              <div className="fg">
                <label className="fl">
                  CIN (Corporate Identity Number) <span className="req">*</span>
                </label>
                <input className="fi" placeholder="U01100MH2021PTC000000" value={company.cin} onChange={(e) => setField("cin", e.target.value)} data-testid="ob-cin" />
              </div>
            </div>

            <div className="fg" style={{ marginTop: 4 }}>
              <label className="fl">
                Which listing are you targeting? <span className="req">*</span>
              </label>
              <div className="choice">
                <button
                  className={`ch${mode === "mb" ? " on" : ""}`}
                  onClick={() => pick("mb")}
                  data-testid="ob-choice-mb"
                >
                  <div className="ch-h">
                    <i className="ti ti-building-skyscraper" aria-hidden="true" /> Main Board IPO
                  </div>
                  <div className="ch-d">NSE / BSE main board. Larger scale, stricter SEBI thresholds.</div>
                </button>
                <button
                  className={`ch${mode === "sme" ? " on" : ""}`}
                  onClick={() => pick("sme")}
                  data-testid="ob-choice-sme"
                >
                  <div className="ch-h">
                    <i className="ti ti-businessplan" aria-hidden="true" /> SME IPO
                  </div>
                  <div className="ch-d">BSE SME / NSE Emerge. Lighter thresholds, 100% underwritten.</div>
                </button>
              </div>
            </div>

            <div className="elig">
              <div className="sec-t" style={{ marginBottom: 8 }}>
                <i className="ti ti-checklist" aria-hidden="true" />{" "}
                <span>{mode === "mb" ? "Main Board" : "SME"} eligibility — quick self-check</span>
              </div>
              <div className="elig-note">
                <i className="ti ti-info-circle" style={{ fontSize: 13, color: "#BA7517" }} aria-hidden="true" />{" "}
                <span>
                  Indicative only — your advisors verify against audited records. Answer to the best
                  of your knowledge.
                </span>
              </div>

              <div data-testid="ob-criteria-list">
                {items.map((c, i) => (
                  <div className="crit" key={`${mode}-${i}`}>
                    <div className="crit-txt">
                      <div className="crit-n">{c.n}</div>
                      <div className="crit-r">{c.r}</div>
                    </div>
                    <div className="crit-toggle">
                      <button
                        className={`tg${answers[i] === "yes" ? " yes" : ""}`}
                        onClick={() => setAns(i, "yes")}
                        data-testid={`ob-crit-${i}-yes`}
                      >
                        Yes
                      </button>
                      <button
                        className={`tg${answers[i] === "no" ? " no" : ""}`}
                        onClick={() => setAns(i, "no")}
                        data-testid={`ob-crit-${i}-no`}
                      >
                        No
                      </button>
                      <button
                        className={`tg${answers[i] === "unsure" ? " unsure" : ""}`}
                        onClick={() => setAns(i, "unsure")}
                        data-testid={`ob-crit-${i}-unsure`}
                      >
                        Unsure
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="ob-foot">
              <span className="score" data-testid="ob-score">
                Eligibility signals:{" "}
                <strong>
                  {met} / {items.length}
                </strong>{" "}
                met
              </span>
              <button className="submit" onClick={submitForm} disabled={saving} data-testid="ob-submit">
                {saving ? "Creating…" : "Create my workspace →"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IPOWorkspaceOnboarding;
